import React, { useState } from 'react';
import { syncAllRapportiniCosts } from '../../services/syncAllRapportiniCosts';
import './SyncRapportiniCosts.css';

const SyncRapportiniCosts = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [isDryRun, setIsDryRun] = useState(true);
  const [syncHours, setSyncHours] = useState(false);
  const [startDate, setStartDate] = useState(''); // 🆕 Data inizio
  const [endDate, setEndDate] = useState('');     // 🆕 Data fine
  const [progress, setProgress] = useState(null);
  const [results, setResults] = useState(null);

  const handleSync = async () => {
    // Validazione date
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      alert('❌ Errore: La data di inizio deve essere precedente alla data di fine!');
      return;
    }

    const dateRangeText = startDate || endDate 
      ? `\n📅 Periodo: ${startDate || 'inizio'} → ${endDate || 'fine'}`
      : '\n📅 Periodo: TUTTI i rapportini';

    const confirmMessage = isDryRun 
      ? `Vuoi eseguire una simulazione (DRY RUN)?${dateRangeText}

Questa operazione mostrerà cosa verrebbe aggiornato senza salvare nulla.

${syncHours ? '✅ Sincronizza ore da WorkHours: ATTIVO' : '⚠️ Sincronizza solo costi, NON le ore'}`
      : `⚠️ ATTENZIONE! Stai per aggiornare REALMENTE tutti i rapportini.${dateRangeText}

${syncHours ? '✅ Sincronizzerà anche le ore da WorkHours' : '⚠️ Sincronizzerà solo i costi, NON le ore'}

Sei sicuro di voler continuare?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsRunning(true);
    setProgress(null);
    setResults(null);

    try {
      const stats = await syncAllRapportiniCosts({
        dryRun: isDryRun,
        onlyZeroCost: true,
        syncHours: syncHours,
        startDate: startDate || null,  // 🆕 Passa data inizio
        endDate: endDate || null,      // 🆕 Passa data fine
        onProgress: (prog) => {
          setProgress(prog);
        }
      });

      setResults(stats);
      
      let message = isDryRun 
        ? `✅ Simulazione completata!\n\nRapportini da aggiornare: ${stats.rapportiniDaAggiornare}\nPersonale da aggiornare: ${stats.personaleAggiornato}`
        : `✅ Sincronizzazione completata!\n\nRapportini aggiornati: ${stats.rapportiniAggiornati}\nPersonale aggiornato: ${stats.personaleAggiornato}\nCosti aggiornati: ${stats.costiAggiornati}`;
      
      if (syncHours) {
        message += `\n\n🕐 Ore aggiornate: ${stats.oreAggiornate || 0}\n⚠️ Discrepanze trovate: ${stats.discrepanzeOre || 0}`;
      }
      
      alert(message);

    } catch (error) {
      console.error('Errore sincronizzazione:', error);
      alert(`❌ Errore: ${error.message}`);
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="sync-rapportini-container">
      <div className="sync-header">
        <h2>🔄 Sincronizzazione Rapportini</h2>
        <p className="text-muted">
          Ricalcola i costi del personale e sincronizza le ore per i rapportini esistenti
        </p>
      </div>

      <div className="sync-options">
        {/* 🆕 FILTRO DATE */}
        <div className="date-filter-section mb-4">
          <h5 className="mb-3">📅 Filtra per Periodo</h5>
          <div className="row">
            <div className="col-md-6">
              <label htmlFor="startDate" className="form-label">
                <strong>Data Inizio</strong>
              </label>
              <input
                type="date"
                id="startDate"
                className="form-control"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={isRunning}
              />
              <small className="text-muted">Lascia vuoto per iniziare dall'inizio</small>
            </div>
            <div className="col-md-6">
              <label htmlFor="endDate" className="form-label">
                <strong>Data Fine</strong>
              </label>
              <input
                type="date"
                id="endDate"
                className="form-control"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={isRunning}
                min={startDate} // Impedisce di selezionare una data precedente a startDate
              />
              <small className="text-muted">Lascia vuoto per includere tutto</small>
            </div>
          </div>
          {startDate && endDate && (
            <div className="alert alert-info mt-2">
              ℹ️ Verranno controllati i rapportini dal <strong>{new Date(startDate).toLocaleDateString('it-IT')}</strong> al <strong>{new Date(endDate).toLocaleDateString('it-IT')}</strong>
            </div>
          )}
          {!startDate && !endDate && (
            <div className="alert alert-warning mt-2">
              ⚠️ Nessun filtro data impostato: verranno controllati <strong>TUTTI</strong> i rapportini nel database
            </div>
          )}
        </div>

        <hr className="my-4" />

        {/* Opzione 1: DRY RUN */}
        <div className="form-check form-switch mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            id="dryRunSwitch"
            checked={isDryRun}
            onChange={(e) => setIsDryRun(e.target.checked)}
            disabled={isRunning}
          />
          <label className="form-check-label" htmlFor="dryRunSwitch">
            <strong>Modalità Simulazione (DRY RUN)</strong>
            <br />
            <small>Se attivo, mostra solo cosa verrebbe modificato senza salvare</small>
          </label>
        </div>

        {/* 🆕 OPZIONE 2: Sincronizza Ore */}
        <div className="form-check form-switch mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            id="syncHoursSwitch"
            checked={syncHours}
            onChange={(e) => setSyncHours(e.target.checked)}
            disabled={isRunning}
          />
          <label className="form-check-label" htmlFor="syncHoursSwitch">
            <strong>🕐 Sincronizza Ore da WorkHours</strong>
            <br />
            <small>
              Se attivo, aggiorna anche le ore del rapportino con quelle del sistema WorkHours.
              {syncHours && <span className="text-warning"> ⚠️ Sovrascrive le ore nel rapportino!</span>}
            </small>
          </label>
        </div>

        {/* Info aggiuntiva quando syncHours è attivo */}
        {syncHours && (
          <div className="alert alert-warning mt-2">
            <strong>⚠️ Attenzione:</strong> Con questa opzione attiva, le ore nel rapportino verranno 
            <strong> sovrascritte</strong> con quelle presenti nel sistema WorkHours. 
            Usa questa opzione solo se sei sicuro che WorkHours sia la fonte corretta.
          </div>
        )}
      </div>

      <div className="sync-actions">
        <button
          className={`btn btn-lg ${isDryRun ? 'btn-info' : 'btn-warning'}`}
          onClick={handleSync}
          disabled={isRunning}
        >
          {isRunning ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              {isDryRun ? 'Simulazione in corso...' : 'Sincronizzazione in corso...'}
            </>
          ) : (
            <>
              {isDryRun ? '🔍 Esegui Simulazione' : '⚠️ Sincronizza Tutti i Rapportini'}
            </>
          )}
        </button>
      </div>

      {progress && (
        <div className="sync-progress mt-4">
          <div className="progress">
            <div
              className="progress-bar progress-bar-striped progress-bar-animated"
              role="progressbar"
              style={{ width: `${progress.percentuale}%` }}
            >
              {progress.percentuale}%
            </div>
          </div>
          <p className="text-center mt-2">
            {progress.processati} / {progress.totali} rapportini processati
          </p>
        </div>
      )}

      {results && (
        <div className="sync-results mt-4">
          <div className="card">
            <div className="card-header bg-success text-white">
              <h5 className="mb-0">📊 Risultati</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="result-item">
                    <span className="result-label">Rapportini totali:</span>
                    <span className="result-value">{results.totaleRapportini}</span>
                  </div>
                  <div className="result-item">
                    <span className="result-label">Rapportini da aggiornare:</span>
                    <span className="result-value text-warning">
                      {results.rapportiniDaAggiornare}
                    </span>
                  </div>
                  <div className="result-item">
                    <span className="result-label">Rapportini aggiornati:</span>
                    <span className="result-value text-success">
                      {results.rapportiniAggiornati}
                    </span>
                  </div>
                  <div className="result-item">
                    <span className="result-label">Personale aggiornato:</span>
                    <span className="result-value text-info">
                      {results.personaleAggiornato}
                    </span>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="result-item">
                    <span className="result-label">Costi aggiornati:</span>
                    <span className="result-value text-primary">
                      {results.costiAggiornati || 0}
                    </span>
                  </div>
                  {syncHours && (
                    <>
                      <div className="result-item">
                        <span className="result-label">Ore aggiornate:</span>
                        <span className="result-value text-info">
                          {results.oreAggiornate || 0}
                        </span>
                      </div>
                      <div className="result-item">
                        <span className="result-label">Discrepanze ore:</span>
                        <span className="result-value text-warning">
                          {results.discrepanzeOre || 0}
                        </span>
                      </div>
                    </>
                  )}
                  <div className="result-item">
                    <span className="result-label">Errori:</span>
                    <span className="result-value text-danger">
                      {results.errori}
                    </span>
                  </div>
                </div>
              </div>

              {results.dettagli && results.dettagli.length > 0 && (
                <div className="mt-3">
                  <h6>Dettaglio aggiornamenti (primi 10):</h6>
                  <ul className="list-group">
                    {results.dettagli.slice(0, 10).map((d, i) => (
                      <li key={i} className="list-group-item">
                        <strong>{d.persona}</strong>: 
                        <span className="text-muted"> €{d.vecchioCosto.toFixed(2)}</span>
                        <span className="mx-2">→</span>
                        <span className="text-success">€{d.nuovoCosto.toFixed(2)}</span>
                        <small className="text-muted ms-2">
                          ({d.ore}h × €{d.costoOrario}/h)
                        </small>
                        {d.oreAggiornate && (
                          <span className="badge bg-warning ms-2">ORE AGGIORNATE</span>
                        )}
                      </li>
                    ))}
                  </ul>
                  {results.dettagli.length > 10 && (
                    <p className="text-muted mt-2">
                      ... e altri {results.dettagli.length - 10} aggiornamenti
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="sync-info mt-4">
        <div className="alert alert-info">
          <h6>ℹ️ Come funziona:</h6>
          <ul>
            <li>Lo script cerca tutti i rapportini attivi</li>
            <li>Per ogni persona nel rapportino, recupera il costo orario dal database utenti</li>
            <li>Ricalcola il totale: <code>oreLavorate × costoOrario</code></li>
            <li>
              {syncHours ? (
                <strong>🕐 Sincronizza le ore dal sistema WorkHours (se diverse dal rapportino)</strong>
              ) : (
                'Aggiorna solo i costi, NON le ore'
              )}
            </li>
            <li>Aggiorna solo i rapportini dove il costo è zero ma ci sono ore lavorate</li>
            <li>Usa batch updates per performance ottimali (50 rapportini per batch)</li>
          </ul>
        </div>

        <div className="alert alert-warning">
          <h6>⚠️ Importante:</h6>
          <ul>
            <li>Esegui sempre prima una <strong>simulazione (DRY RUN)</strong></li>
            <li>Verifica i risultati prima di eseguire l'aggiornamento reale</li>
            {syncHours && (
              <li className="text-danger">
                <strong>Con "Sincronizza Ore" attivo:</strong> Le ore nei rapportini verranno 
                sovrascritte con quelle di WorkHours. Usa solo se sei sicuro!
              </li>
            )}
            <li>L'operazione può richiedere alcuni minuti se hai molti rapportini</li>
            <li>Non chiudere la finestra durante l'operazione</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SyncRapportiniCosts;